import { errorMsg } from '../../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, TextField, InputAdornment, CircularProgress,
  Alert, List, ListItem, ListItemText, Paper, Chip, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  FormControl, InputLabel, Select, MenuItem,
  IconButton, Divider, useMediaQuery, useTheme,
  SwipeableDrawer, Fab, ListItemAvatar, Badge
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ContextNavBar from '../../components/ContextNavBar';
import api from '../../axios';
import { getJornadaMembro, atualizarJornada } from '../../api/jornada';

const DARK = '#1C1C1E';
const LIME = '#00F1A7';

const ESTADO_CIVIL_OPCOES = [
  'Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viuvo(a)',
];

const montarTimeline = (dados, filhos) => {
  const etapas = [];

  if (dados.jornada?.igreja_origem) {
    etapas.push({
      id: 'origem', titulo: 'Origem',
      descricao: dados.jornada.igreja_origem,
      icone: '\u{1F3E0}', cor: '#00F1A7',
      data: null, campo: 'igreja_origem'
    });
  }

  if (dados.jornada?.data_primeira_visita) {
    etapas.push({
      id: 'primeira_visita', titulo: 'Primeira Visita',
      descricao: dados.jornada.data_primeira_visita.slice(0, 10),
      icone: '\u{1F44B}', cor: '#3B82F6',
      data: dados.jornada.data_primeira_visita.slice(0, 10),
      campo: 'data_primeira_visita'
    });
  }

  if (dados.jornada?.data_decisao) {
    etapas.push({
      id: 'decisao', titulo: 'Decisão',
      descricao: dados.jornada.data_decisao.slice(0, 10),
      icone: '\u{1F64F}', cor: '#22C55E',
      data: dados.jornada.data_decisao.slice(0, 10),
      campo: 'data_decisao'
    });
  }

  if (dados.membro?.fez_start === 'Sim') {
    etapas.push({
      id: 'fez_start', titulo: 'Fez o Start',
      descricao: 'Sim',
      icone: '\u{1F393}', cor: '#10B981',
      data: null, campo: 'fez_start'
    });
  }

  if (dados.jornada?.data_batismo) {
    etapas.push({
      id: 'batismo', titulo: 'Batismo',
      descricao: dados.jornada.data_batismo.slice(0, 10),
      icone: '\u{1F4A7}', cor: '#06B6D4',
      data: dados.jornada.data_batismo.slice(0, 10),
      campo: 'data_batismo'
    });
  }

  if (dados.jornada?.estado_civil) {
    etapas.push({
      id: 'estado_civil', titulo: 'Estado Civil',
      descricao: `${dados.jornada.estado_civil}${dados.jornada.conjuge_nome ? ' • ' + dados.jornada.conjuge_nome : ''}`,
      icone: '\u{1F48D}', cor: '#EC4899',
      data: null, campo: 'estado_civil'
    });
  }

  if (filhos?.length > 0) {
    filhos.forEach(f => {
      etapas.push({
        id: `filho_${f.id}`, titulo: 'Filho',
        descricao: `${f.nome}${f.data_nascimento ? ' (' + f.data_nascimento.slice(0, 10) + ')' : ''}`,
        icone: '\u{1F476}', cor: '#F472B6',
        data: f.data_nascimento ? f.data_nascimento.slice(0, 10) : null,
        campo: `filho_${f.id}`, isFilho: true, filhoId: f.id
      });
    });
  }

  etapas.push({
    id: 'membro', titulo: 'Recebido como Membro',
    descricao: 'Sim',
    icone: '\u2705', cor: '#10B981',
    data: new Date().toISOString().slice(0, 10),
    campo: 'data_migracao'
  });

  if (dados.discipulador?.nome) {
    etapas.push({
      id: 'discipulador', titulo: 'Discipulador',
      descricao: dados.discipulador.nome,
      icone: '\u{1F464}', cor: '#F59E0B',
      data: null, campo: 'discipulador'
    });
  }

  if (dados.gc_atual?.nome) {
    etapas.push({
      id: 'gc', titulo: 'Grupo de Crescimento',
      descricao: `${dados.gc_atual.nome} • Líder: ${dados.gc_atual.lider_nome || 'N/A'}`,
      icone: '\u{1F3D8}\uFE0F', cor: '#14B8A6',
      data: null, campo: 'gc'
    });
  }

  if (dados.cursos?.length > 0) {
    dados.cursos.forEach(c => {
      etapas.push({
        id: `curso_${c.id}`, titulo: 'Curso',
        descricao: c.titulo,
        icone: '\u{1F4DA}', cor: '#00C48C',
        data: c.data_fim, campo: 'curso'
      });
    });
  }

  if (dados.funcao_atual || dados.jornada?.funcao_atual) {
    etapas.push({
      id: 'funcao', titulo: 'Função Atual',
      descricao: dados.funcao_atual || dados.jornada.funcao_atual,
      icone: '\u2B50', cor: '#DC2626',
      data: null, campo: 'funcao_atual'
    });
  }

  return etapas;
};

function ListaMembros({ membrosFiltrados, selectedMembro, onSelect, busca, setBusca, onClose, isMobile }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <TextField
          placeholder="Buscar membro..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          fullWidth
          size="small"
          autoFocus={isMobile}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {isMobile && (
          <IconButton onClick={onClose} size="small" aria-label="fechar lista">
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <Divider />
      <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
        {membrosFiltrados.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">Nenhum membro encontrado</Typography>
          </Box>
        ) : membrosFiltrados.map(m => (
          <ListItem
            key={m.id}
            button
            onClick={() => onSelect(m)}
            sx={{
              borderRadius: 0,
              borderBottom: '1px solid',
              borderBottomColor: 'divider',
              bgcolor: selectedMembro?.id === m.id ? 'action.selected' : 'transparent',
              borderLeft: '3px solid',
              borderLeftColor: selectedMembro?.id === m.id ? LIME : 'transparent',
              '&:hover': { bgcolor: '#f5f5f5' },
            }}
          >
            <ListItemAvatar sx={{ minWidth: 48 }}>
              <Avatar sx={{ bgcolor: LIME, color: DARK, width: 40, height: 40, fontSize: 16, fontWeight: 700 }}>
                {m.nome?.charAt(0)?.toUpperCase() || '?'}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={m.nome}
              secondary={m.email}
              primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600, color: DARK } }}
              secondaryTypographyProps={{ variant: 'caption', noWrap: true, sx: { display: 'block' } }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

function ResumoChips({ jornadaData, horizontal = false }) {
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: horizontal ? 'row' : 'column',
      gap: 1,
      flexWrap: horizontal ? 'nowrap' : 'wrap',
      overflowX: horizontal ? 'auto' : 'visible',
      pb: horizontal ? 1 : 0,
      '&::-webkit-scrollbar': { height: 4 },
      '&::-webkit-scrollbar-thumb': { bgcolor: '#ddd', borderRadius: 2 },
    }}>
      {jornadaData?.discipulador && (
        <Chip
          icon={<span>{'\u{1F464}'}</span>}
          label={`Discipulador: ${jornadaData.discipulador.nome}`}
          variant="outlined" size="small"
          sx={{ flexShrink: 0 }}
        />
      )}
      {jornadaData?.gc_atual && (
        <Chip
          icon={<span>{'\u{1F3D8}\uFE0F'}</span>}
          label={`GC: ${jornadaData.gc_atual.nome}`}
          variant="outlined" size="small"
          sx={{ flexShrink: 0 }}
        />
      )}
      <Chip
        icon={<span>{'\u{1F4DA}'}</span>}
        label={`${jornadaData?.cursos?.length || 0} cursos`}
        variant="outlined" size="small"
        sx={{ flexShrink: 0 }}
      />
      {jornadaData?.funcao_atual && (
        <Chip
          icon={<span>{'\u{2B50}'}</span>}
          label={jornadaData.funcao_atual}
          variant="outlined" size="small"
          sx={{ flexShrink: 0 }}
        />
      )}
    </Box>
  );
}

export default function JornadaLista() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [membros, setMembros] = useState([]);
  const [busca, setBusca] = useState('');
  const [selectedMembro, setSelectedMembro] = useState(null);
  const [jornadaData, setJornadaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [etapas, setEtapas] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);

  const [listaOpen, setListaOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);

  useEffect(() => {
    api.get('/secretaria/membros')
      .then(r => setMembros(r.data || []))
      .catch(() => {});
  }, []);

  const carregarJornada = async (membroId) => {
    setLoading(true);
    setErro('');
    try {
      const [jornada, filhosData] = await Promise.all([
        getJornadaMembro(membroId),
        api.get(`/jornada/membro/${membroId}/filhos`).then(r => r.data || []).catch(() => []),
      ]);
      setJornadaData(jornada);
      const todas = montarTimeline(jornada, filhosData);
      todas.sort((a, b) => {
        if (!a.data && !b.data) return 0;
        if (!a.data) return 1;
        if (!b.data) return -1;
        return b.data.localeCompare(a.data);
      });
      setEtapas(todas);
    } catch {
      setErro('Erro ao carregar jornada');
    }
    setLoading(false);
  };

  const selecionarMembro = (m) => {
    setSelectedMembro(m);
    carregarJornada(m.id);
    if (isMobile) setListaOpen(false);
  };

  const membrosFiltrados = membros.filter(m =>
    !busca || m.nome?.toLowerCase().includes(busca.toLowerCase())
  );

  const handleDragStart = (i) => { setDragIndex(i); };
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = (i) => {
    if (dragIndex === null || dragIndex === i) return;
    const nova = [...etapas];
    const [removido] = nova.splice(dragIndex, 1);
    nova.splice(i, 0, removido);
    setEtapas(nova);
    setDragIndex(null);
  };

  const moverEtapa = (i, delta) => {
    const novo = i + delta;
    if (novo < 0 || novo >= etapas.length) return;
    const nova = [...etapas];
    [nova[i], nova[novo]] = [nova[novo], nova[i]];
    setEtapas(nova);
  };

  const editarEtapa = (etapa) => {
    setEditItem(etapa);
    setEditVal(etapa.descricao || '');
    setEditOpen(true);
  };

  const salvarEdicao = async () => {
    if (!selectedMembro || !editItem) return;
    setSaving(true);
    try {
      const payload = {};
      payload[editItem.campo] = editVal;
      await atualizarJornada(selectedMembro.id, payload);
      setEditOpen(false);
      carregarJornada(selectedMembro.id);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao salvar');
    }
    setSaving(false);
  };

  const timelineContent = (
    !selectedMembro ? (
      <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
        <Typography variant="h6">Selecione um membro para ver sua jornada</Typography>
      </Box>
    ) : loading ? (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    ) : erro ? (
      <Alert severity="error">{erro}</Alert>
    ) : (
      <>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, mb: 2,
          position: 'sticky', top: 0, bgcolor: 'background.paper',
          zIndex: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider'
        }}>
          {isMobile && (
            <IconButton onClick={() => setSelectedMembro(null)} size="small" aria-label="voltar">
              <CloseIcon />
            </IconButton>
          )}
          <Avatar sx={{ bgcolor: LIME, color: DARK, width: 40, height: 40, fontSize: 16, fontWeight: 700 }}>
            {jornadaData?.nome?.charAt(0)?.toUpperCase() || '?'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1rem', md: '1.25rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {jornadaData?.nome}
            </Typography>
            {jornadaData?.email && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {jornadaData.email}
              </Typography>
            )}
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/jornada/membro/${selectedMembro.id}`)}
            sx={{ flexShrink: 0 }}
          >
            Perfil
          </Button>
        </Box>

        {etapas.length === 0 ? (
          <Alert severity="info">
            Nenhuma informação de jornada registrada para este membro.
          </Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                size="small"
                startIcon={<SwapVertIcon />}
                onClick={() => setReorderOpen(true)}
              >
                Reordenar
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
              <Box sx={{ flex: 1, position: 'relative', pl: { xs: 3, md: 4 } }}>
                <Box sx={{
                  position: 'absolute', left: { xs: 16, md: 32 }, top: 0, bottom: 0,
                  width: 3, bgcolor: '#e5e7eb', borderRadius: 2
                }} />

                {etapas.map((etapa, i) => (
                  <Box
                    key={etapa.id || i}
                    sx={{
                      position: 'relative', mb: 2.5, ml: 2,
                      cursor: { xs: 'default', md: 'grab' },
                      opacity: dragIndex === i ? 0.5 : 1
                    }}
                    draggable={!isMobile}
                    onDragStart={!isMobile ? () => handleDragStart(i) : undefined}
                    onDragOver={!isMobile ? handleDragOver : undefined}
                    onDrop={!isMobile ? () => handleDrop(i) : undefined}
                  >
                    <Box
                      onClick={() => editarEtapa(etapa)}
                      sx={{
                        position: 'absolute', left: { xs: -22, md: -30 }, top: 4,
                        width: { xs: 36, md: 32 }, height: { xs: 36, md: 32 },
                        borderRadius: '50%',
                        bgcolor: etapa.cor, color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: { xs: 16, md: 14 }, cursor: 'pointer', zIndex: 1,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                        '&:hover': { transform: 'scale(1.15)', transition: '0.2s' }
                      }}
                    >
                      {etapa.icone}
                    </Box>

                    <Box sx={{
                      ml: { xs: 1, md: 2 }, p: { xs: 1.5, md: 1.5 }, borderRadius: 2,
                      bgcolor: '#f9fafb', border: '1px solid', borderColor: '#e5e7eb',
                      '&:hover': { bgcolor: '#f3f4f6' }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: DARK }}>
                          {etapa.titulo}
                        </Typography>
                        {etapa.data && (
                          <Typography variant="caption" sx={{ color: etapa.cor, fontWeight: 700, bgcolor: etapa.cor + '15', px: 0.75, py: 0.25, borderRadius: 1 }}>
                            {etapa.data.split('-').reverse().join('/')}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                        {etapa.descricao}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, mb: { xs: 2, md: 0 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Resumo</Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: 1, overflowX: 'auto', pb: { xs: 1, md: 0 } }}>
                  <ResumoChips jornadaData={jornadaData} horizontal={isMobile} />
                </Box>
              </Box>
            </Box>
          </>
        )}
      </>
    )
  );

  return (
    <>
      <Header />
      <ContextNavBar context="jornada" />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 }, minHeight: '70vh' }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
          Jornada dos Membros
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {!isMobile && (
            <Paper sx={{ width: 300, flexShrink: 0, borderRadius: 2, overflow: 'hidden' }} elevation={2}>
              <ListaMembros
                membrosFiltrados={membrosFiltrados}
                selectedMembro={selectedMembro}
                onSelect={selecionarMembro}
                busca={busca}
                setBusca={setBusca}
                onClose={() => setListaOpen(false)}
                isMobile={false}
              />
            </Paper>
          )}

          <Paper sx={{ flex: 1, p: { xs: 1.5, md: 3 }, borderRadius: 2 }} elevation={2}>
            {timelineContent}
          </Paper>
        </Box>
      </Container>

      {isMobile && (
        <>
          <SwipeableDrawer
            anchor="bottom"
            open={listaOpen}
            onOpen={() => setListaOpen(true)}
            onClose={() => setListaOpen(false)}
            disableEnforceFocus
            PaperProps={{
              sx: {
                height: '85vh',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                overflow: 'hidden'
              }
            }}
          >
            <Box sx={{
              width: 40, height: 4, bgcolor: '#ccc', borderRadius: 2,
              mx: 'auto', mt: 1, mb: 0.5, flexShrink: 0
            }} />
            <ListaMembros
              membrosFiltrados={membrosFiltrados}
              selectedMembro={selectedMembro}
              onSelect={selecionarMembro}
              busca={busca}
              setBusca={setBusca}
              onClose={() => setListaOpen(false)}
              isMobile={true}
            />
          </SwipeableDrawer>

          {!selectedMembro && !listaOpen && (
            <Fab
              color="primary"
              aria-label="buscar membro"
              onClick={() => setListaOpen(true)}
              sx={{
                position: 'fixed', bottom: 80, right: 16, zIndex: 1200,
                bgcolor: LIME, color: DARK, fontWeight: 700,
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                '&:hover': { bgcolor: LIME, opacity: 0.9 }
              }}
            >
              <Badge color="error" variant="dot" invisible={membros.length === 0}>
                <PersonSearchIcon />
              </Badge>
            </Fab>
          )}

          {selectedMembro && !listaOpen && (
            <Fab
              size="small"
              variant="extended"
              aria-label="trocar membro"
              onClick={() => setListaOpen(true)}
              sx={{
                position: 'fixed', bottom: 80, right: 16, zIndex: 1200,
                bgcolor: 'background.paper', color: DARK, fontWeight: 600,
                border: '1px solid', borderColor: 'divider',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              <PersonSearchIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Trocar
            </Fab>
          )}
        </>
      )}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar: {editItem?.titulo}</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {editItem?.campo === 'igreja_origem' && (
            <TextField
              label="Igreja de Origem"
              value={editVal}
              onChange={e => setEditVal(e.target.value)}
              fullWidth
            />
          )}
          {editItem?.campo === 'data_primeira_visita' && (
            <TextField
              label="Data"
              type="date"
              value={editVal}
              onChange={e => setEditVal(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          )}
          {editItem?.campo === 'data_decisao' && (
            <TextField
              label="Data"
              type="date"
              value={editVal}
              onChange={e => setEditVal(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          )}
          {editItem?.campo === 'data_batismo' && (
            <TextField
              label="Data"
              type="date"
              value={editVal}
              onChange={e => setEditVal(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          )}
          {editItem?.campo === 'estado_civil' && (
            <FormControl fullWidth>
              <InputLabel>Estado Civil</InputLabel>
              <Select
                value={editVal}
                label="Estado Civil"
                onChange={e => setEditVal(e.target.value)}
              >
                {ESTADO_CIVIL_OPCOES.map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {editItem?.campo === 'funcao_atual' && (
            <TextField
              label="Função Atual"
              value={editVal}
              onChange={e => setEditVal(e.target.value)}
              fullWidth
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarEdicao} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reorderOpen} onClose={() => setReorderOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reordenar Etapas</DialogTitle>
        <DialogContent sx={{ p: 1 }}>
          <List dense>
            {etapas.map((etapa, i) => (
              <ListItem
                key={etapa.id || i}
                sx={{
                  borderRadius: 1, mb: 0.5,
                  border: '1px solid', borderColor: 'divider'
                }}
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      disabled={i === 0}
                      onClick={() => moverEtapa(i, -1)}
                      aria-label="mover para cima"
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={i === etapas.length - 1}
                      onClick={() => moverEtapa(i, 1)}
                      aria-label="mover para baixo"
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              >
                <Avatar sx={{ bgcolor: etapa.cor, color: '#fff', width: 32, height: 32, fontSize: 14, mr: 1.5 }}>
                  {etapa.icone}
                </Avatar>
                <ListItemText
                  primary={etapa.titulo}
                  secondary={etapa.descricao}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: 'caption', noWrap: true, display: 'block' }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReorderOpen(false)} variant="contained">Pronto</Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </>
  );
}
